import { useLayoutEffect, useRef, useState, type RefObject } from "react"

import { CopyButton } from "@/components/common/CopyButton"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select"

type EmbedTheme = "light" | "dark" | "auto"
type EmbedSize = "sm" | "md" | "lg"

type EmbedFrameSize = {
  width: number
  height: number
}

type EmbedSectionProps = {
  baseBorder: string
  embedTheme: EmbedTheme
  systemPrefersDark: boolean
  embedFrameSize: EmbedFrameSize
  previewPadding: number
  embedPreviewHeight: number
  isEmbedReady: boolean
  hasLogos: boolean
  embedFrameRef: RefObject<HTMLIFrameElement>
  embedPreviewHtml: string
  handleEmbedFrameLoad: () => void
  embedSize: EmbedSize
  setEmbedTheme: (value: EmbedTheme) => void
  setEmbedSize: (value: EmbedSize) => void
  embedHtml: string
  handleEmbedCopy: () => void
}

export const EmbedSection = ({
  baseBorder,
  embedTheme,
  systemPrefersDark,
  embedFrameSize,
  previewPadding,
  embedPreviewHeight,
  isEmbedReady,
  hasLogos,
  embedFrameRef,
  embedPreviewHtml,
  handleEmbedFrameLoad,
  embedSize,
  setEmbedTheme,
  setEmbedSize,
  embedHtml,
  handleEmbedCopy
}: EmbedSectionProps) => {
  const previewRef = useRef<HTMLDivElement>(null)
  const [previewWidth, setPreviewWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (typeof window === "undefined") return
    const node = previewRef.current
    if (!node) return
    const container = node.closest("[data-widget-container]") as HTMLElement | null
    if (!container) return
    const measure = () => {
      const rect = container.getBoundingClientRect()
      const styles = window.getComputedStyle(container)
      const paddingLeft = 32//Number.parseFloat(styles.paddingLeft) || 0
      const paddingRight = 32//Number.parseFloat(styles.paddingRight) || 0
      const nextWidth = Math.max(0, Math.floor(rect.width - paddingLeft - paddingRight))
      setPreviewWidth(prev => (prev === nextWidth ? prev : nextWidth))
    }
    measure()
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure)
      observer.observe(container)
      return () => observer.disconnect()
    }
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [])

  return (
    <div className="flex h-full min-w-0 flex-col gap-2">
      <div
        ref={previewRef}
        className={`relative min-h-0 min-w-0 w-full max-w-full overflow-hidden rounded-xl ${baseBorder} shadow-inner ${
          embedTheme === "dark" || (embedTheme === "auto" && systemPrefersDark)
            ? "bg-[#050505]"
            : "bg-slate-50"
        }`}
        style={{
          height: embedFrameSize.height ? embedFrameSize.height + previewPadding * 2 + 8 : embedPreviewHeight + previewPadding * 2,
          width: "100%",
          maxHeight: "100%"
        }}
      >
        {!isEmbedReady && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            {hasLogos ? "Preparing preview…" : "Build your license to preview the widget."}
          </div>
        )}
        <div className="h-full min-h-0 min-w-0 w-full max-w-full">
          <div className="h-full min-h-0 min-w-0 w-full max-w-full overflow-x-auto overflow-y-hidden">
            {isEmbedReady && (
              <div
                className="absno block w-max align-top mx-auto"
                style={{
                  width: embedFrameSize.width ? `${embedFrameSize.width + 32}px` : "100%",
                  height: embedFrameSize.height ? `${embedFrameSize.height + 32}px` : embedPreviewHeight + 32
                }}
              >
                <iframe
                  ref={embedFrameRef}
                  title="License widget preview"
                  className="block max-w-none rounded-md bg-transparent"
                  style={{
                    width: embedFrameSize.width ? `${embedFrameSize.width}px` : "100%",
                    height: embedFrameSize.height ? `${embedFrameSize.height}px` : embedPreviewHeight,
                    padding: "16px",
                    boxSizing: "content-box"
                  }}
                  sandbox="allow-scripts allow-same-origin"
                  srcDoc={embedPreviewHtml}
                  onLoad={handleEmbedFrameLoad}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-4 px-2">
        <div className="flex w-fit items-center gap-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Theme</div>
          <Select value={embedTheme} onValueChange={value => setEmbedTheme(value as EmbedTheme)}>
            <SelectTrigger className="h-7 border-0 bg-transparent px-1.5 text-xs shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0">
              <SelectValue placeholder="Theme" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">Light</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
              <SelectItem value="auto">Auto</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-fit items-center gap-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Size</div>
          <Select value={embedSize} onValueChange={value => setEmbedSize(value as EmbedSize)}>
            <SelectTrigger className="h-7 border-0 bg-transparent px-1.5 text-xs shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0">
              <SelectValue placeholder="Size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sm">Small</SelectItem>
              <SelectItem value="md">Medium</SelectItem>
              <SelectItem value="lg">Large</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex min-h-[1.6em] flex-1 overflow-hidden">
        <div className="flex h-full min-h-0 flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Embed HTML</span>
            <CopyButton value={embedHtml} iconOnly />
          </div>
          <div
            className="min-h-[1.6em] w-full flex-1 cursor-pointer select-text overflow-auto whitespace-pre-wrap break-words rounded-lg border border-slate-200/70 bg-slate-50/70 px-3 py-2 font-mono text-[11px] leading-snug text-slate-700 hover:border-slate-300"
            role="button"
            tabIndex={0}
            onClick={handleEmbedCopy}
            onKeyDown={event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                handleEmbedCopy()
              }
            }}
            title="Click to copy"
          >
            {embedHtml}
          </div>
        </div>
      </div>
    </div>
  )
}
